import { tracked } from '@glimmer/tracking';

import type { APIData } from 'webapp-types';
import { SheetColumnType } from 'webapp/enums/enums';
import SheetSelection from 'webapp/utils/sheet-selection';

import Serviced from 'webapp/models-custom/serviced';

export type ColumnTypes = Record<
	string,
	{
		type: 'metric' | 'date' | 'dimension';
		columnTitle?: string;
	}
>;

interface ParsedSheetData {
	'!sheetFormat'?: {
		row: number | null;
		col: number | null;
	};
	'!rows'?: (number | null)[];
	'!cols'?: (number | null)[];
	'!merges'?: {
		from: { col: number; row: number };
		to: { col: number; row: number };
	}[];
}

interface ParsedSingleCellData {
	col: number;
	row: number;
	val: string;
	className: string;
	styles: Record<string, string>;
	wordWrap?: boolean;
}

type ParsedCellsData = Record<string, ParsedSingleCellData>;

export default class DataSheet extends Serviced {
	id: string;
	order: number;
	columnTypes: ColumnTypes;
	isFirst = false;

	sheetData!: ParsedSheetData;
	cellData!: ParsedCellsData;
	maxRow = 0;
	maxCol = 0;

	isTrimmed = false;

	@tracked isActive = false;

	constructor(id: string, data: APIData.CustomQuery.Sheet.SheetData) {
		super();

		this.id = id;

		this.order = data.order || 0;
		delete data.order;

		this.columnTypes = data.columnTypes || {};
		delete data.columnTypes;

		this.parseData(data);
	}

	get sheetPrefix(): string {
		let prefix = '';
		if (!this.isFirst) {
			prefix = "'" + this.id + "'!";
		}
		return prefix;
	}

	getColumnTitle(a1Selection: string): string {
		return this.columnTypes[a1Selection]?.columnTitle || '';
	}

	private parseData(data: APIData.CustomQuery.Sheet.SheetData) {
		const sheetData: ParsedSheetData = {},
			cellData: ParsedCellsData = {};

		let maxRow = 0,
			maxCol = 0;

		Object.entries(data).forEach((item) => {
			const [key, value] = item;

			if (key.startsWith('!')) {
				// sheet props are marked with !
				// each one is read from data by its key, which types it
				// https://docs.sheetjs.com/#sheet-objects
				switch (key) {
					case '!sheetFormat': {
						const value1 = data['!sheetFormat'];
						sheetData[key] = {
							row: value1?.row ? value1.row.hpx : null,
							col: value1?.col ? value1.col.wpx : null,
						};
						break;
					}
					case '!rows': {
						const value1 = data['!rows'];
						sheetData[key] = value1!.map((v) => (v ? v.hpx : null));
						break;
					}
					case '!cols': {
						const value1 = data['!cols'];
						sheetData[key] = value1!.map((v) => (v ? v.wpx : null));
						break;
					}
					case '!merges': {
						const value1 = data['!merges'];
						sheetData[key] = value1!.map((v) => {
							return {
								from: { col: v.s.c, row: v.s.r },
								to: { col: v.e.c, row: v.e.r },
							};
						});
						break;
					}
					case '!isTrimmed': {
						const value1 = data['!isTrimmed'];
						this.isTrimmed = !!value1;
						break;
					}
					default:
						break;
				}
			} else {
				const rawCellData = value;
				// cell data
				// https://docs.sheetjs.com/#cell-object
				const coords = SheetSelection.transformCellA1ToIndex(key);
				cellData[key] = Object.assign(this.parseCellProps(rawCellData.s || {}), {
					col: coords.col,
					row: coords.row,
					val: rawCellData.w, // formatted value
				});
				if (coords.col > maxCol) {
					maxCol = coords.col;
				}

				if (coords.row > maxRow) {
					maxRow = coords.row;
				}
			}
		});

		this.sheetData = sheetData;
		this.cellData = cellData;
		this.maxCol = maxCol;
		this.maxRow = maxRow;
	}

	private parseCellProps(styles: APIData.CustomQuery.Sheet.CellData['s']) {
		const c: string[] = [], // classes
			s: Record<string, string> = {}, // styles
			o: { wordWrap?: boolean } = {}; // others, special props

		// set clip as default unless specified otherwise
		o.wordWrap = false;

		if (styles.alignment) {
			switch (styles.alignment.horizontal) {
				case 'left':
					c.push('htLeft');
					break;
				case 'center':
					c.push('htCenter');
					break;
				case 'right':
					c.push('htRight');
					break;
				case 'justify':
					c.push('htJustify');
					break;
				default:
					break;
			}
			switch (styles.alignment.vertical) {
				case 'top':
					c.push('htTop');
					break;
				case 'middle':
					c.push('htMiddle');
					break;
				case 'bottom':
					c.push('htBottom');
					break;
				default:
					break;
			}

			if (styles.alignment.wrapText) {
				o.wordWrap = true;
			}
		}

		// color and fgColor seem to be the same thing
		if (styles.color?.rgb) {
			s.color = '#' + styles.color.rgb;
		}

		if (styles.bgColor?.rgb) {
			s.backgroundColor = '#' + styles.bgColor.rgb;
		}

		if (styles.sz) {
			s.fontSize = styles.sz + 'px';
		}

		if (styles.name) {
			s.fontFamily = styles.name + ',sans-serif';
		}

		if (styles.underline) {
			s.textDecoration = 'underline';
		} else if (styles.strike) {
			s.textDecoration = 'line-through';
		}

		if (styles.italic) {
			s.fontStyle = 'italic';
		}

		if (styles.bold) {
			s.fontWeight = 'bold';
		}

		// set it on cellData
		const cellData: Omit<ParsedSingleCellData, 'col' | 'row' | 'val'> = {
			className: c.join(' '),
			styles: s,
		};

		if (typeof o.wordWrap !== 'undefined') {
			cellData.wordWrap = o.wordWrap;
		}

		return cellData;
	}

	private getColumnsForType(type: string): string[] {
		const columns: string[] = [];
		Object.keys(this.columnTypes).forEach((column) => {
			const columnData = this.columnTypes[column]!;
			if (columnData.type === type) {
				columns.push(column);
			}
		});
		return columns;
	}

	get columnsForMetric(): string[] {
		return this.getColumnsForType(SheetColumnType.METRIC);
	}

	get columnsForDate(): string[] {
		return this.getColumnsForType(SheetColumnType.DATE);
	}

	get columnsForDimension(): string[] {
		return this.getColumnsForType(SheetColumnType.DIMENSION);
	}

	get colWidths(): number[] {
		const defaultColumnWidth = this.sheetData['!sheetFormat']?.col || 50,
			colWidths = Array.from({ length: this.maxCol + 1 }).fill(
				defaultColumnWidth,
			) as number[];

		(this.sheetData['!cols'] || []).forEach((width, idx) => {
			if (width) {
				colWidths[idx] = width;
			}
		});

		return colWidths;
	}

	get rowHeights(): number[] {
		const absoluteMinHeight = 23, // because of https://github.com/handsontable/handsontable/issues/3895
			defaultRowHeight = Math.max(
				this.sheetData['!sheetFormat']?.row || 0,
				absoluteMinHeight,
			),
			rowHeights = Array.from({ length: this.maxRow + 1 }).fill(defaultRowHeight) as number[];

		(this.sheetData['!rows'] || []).forEach((height, idx) => {
			if (height && height >= absoluteMinHeight) {
				rowHeights[idx] = height;
			}
		});

		return rowHeights;
	}

	get mergeCells() {
		return (this.sheetData['!merges'] || []).map((merge) => {
			return {
				row: merge.from.row,
				col: merge.from.col,
				rowspan: merge.to.row - merge.from.row + 1,
				colspan: merge.to.col - merge.from.col + 1,
			};
		});
	}

	get dataForRender(): string[][] {
		const data = [],
			maxRow = this.maxRow,
			maxCol = this.maxCol;

		for (let i = 0; i <= maxRow; i++) {
			const row = [];
			for (let j = 0; j <= maxCol; j++) {
				const a1 = SheetSelection.transformCellIndexToA1({ row: i, col: j }),
					cell = this.cellData[a1];
				if (cell) {
					row[j] = cell.val;
				} else {
					row[j] = '';
				}
			}
			data[i] = row;
		}

		return data;
	}
}
